/**
 * Cookie-based authentication helper for SSO support
 * Supports region-specific cookies: global, eu, ja, staging, demo
 */

import jwtDecode from 'jwt-decode';

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function isTokenWellFormed(token) {
  if (!token || typeof token !== 'string') return false;
  if (token.split('.').length !== 3) return false;

  try {
    const payload = jwtDecode(token);
    if (!payload.sub || !payload.exp) return false;

    const now = Math.floor(Date.now() / 1000);
    if (payload.exp < now) return false;

    return true;
  } catch {
    return false;
  }
}

function getRegionFromHostname(hostname) {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost';
  if (hostname === 'roi-ai.app') return 'global';
  if (hostname === 'enrichment.roi-ai.app') return null; // Enrichment doesn't set cookies

  // AD-531: the Ainstein enrichment app also runs on the ai-nstein.app registrable
  // domain. The portal's shared .roi-ai.app SSO cookie cannot cross that boundary,
  // so sign-in there uses the one-time hand-off code (see helpers/ainsteinLaunch),
  // not a region cookie. Treat the whole ai-nstein.app family as cookie-less here so
  // no wrong-region cookie is ever minted for it.
  if (hostname === 'ai-nstein.app' || hostname.endsWith('.ai-nstein.app')) return null;

  // Extract subdomain: eu.roi-ai.app → 'eu'
  const parts = hostname.split('.');
  if (parts.length >= 3) return parts[0];

  return 'unknown';
}

export function getCookieName(hostname) {
  const region = getRegionFromHostname(hostname);
  return region ? `roi_auth_token_${region}` : null;
}

function getRegionCookie() {
  const cookieName = getCookieName(window.location.hostname);
  return cookieName ? getCookie(cookieName) : null;
}

export function initializeCookieAuth() {
  const cookieToken = getRegionCookie();
  const localStorageToken = localStorage.getItem('token');

  if (cookieToken) {
    if (!isTokenWellFormed(cookieToken)) {
      clearCookieAuth();
      return null;
    }

    if (!localStorageToken) {
      localStorage.setItem('token', JSON.stringify(cookieToken));
    }
  }

  return cookieToken || (localStorageToken ? JSON.parse(localStorageToken) : null);
}

export function setCookieAuth(token) {
  if (!token) return;

  const hostname = window.location.hostname;
  const cookieName = getCookieName(hostname);
  if (!cookieName) return; // e.g., enrichment doesn't set cookies

  const maxAge = 7 * 24 * 60 * 60; // 7 days

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    document.cookie = `${cookieName}=${token}; path=/; domain=localhost; max-age=${maxAge}; SameSite=Lax`;
  } else if (hostname.includes('roi-ai.app')) {
    document.cookie = `${cookieName}=${token}; path=/; domain=.roi-ai.app; max-age=${maxAge}; SameSite=Lax; Secure`;
  } else {
    document.cookie = `${cookieName}=${token}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }
}

export function clearCookieAuth() {
  localStorage.removeItem('token');

  const hostname = window.location.hostname;
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
  const isProduction = hostname.includes('roi-ai.app');

  // Clear all region-specific cookies to ensure complete logout
  const regions = ['global', 'eu', 'ja', 'staging', 'demo', 'localhost'];

  regions.forEach(region => {
    const cookieName = `roi_auth_token_${region}`;

    // Clear without domain
    document.cookie = `${cookieName}=; ${expiry}; path=/;`;

    // Clear with domain
    if (isProduction) {
      document.cookie = `${cookieName}=; ${expiry}; path=/; domain=.roi-ai.app;`;
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      document.cookie = `${cookieName}=; ${expiry}; path=/; domain=localhost;`;
    }
  });
}
